import { z } from 'zod'

/** IANA zone names are validated properly server-side; this catches typos. */
const timezoneSchema = z
  .string()
  .min(1, 'Pick a timezone')
  .max(100)
  .regex(/^[A-Za-z_+-]+(\/[A-Za-z0-9_+-]+)*$/, 'Not a valid timezone identifier')

export const orgSlugSchema = z
  .string()
  .trim()
  .min(3, 'At least 3 characters')
  .max(60, 'At most 60 characters')
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Lowercase letters, numbers and single hyphens only')

export const createOrganizationSchema = z.object({
  name: z.string().trim().min(2, 'Enter the church name').max(200),
  slug: orgSlugSchema,
})
export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>

export const updateOrganizationSchema = z.object({
  name: z.string().trim().min(2, 'Enter the church name').max(200).optional(),
  timezone: timezoneSchema.optional(),
  logo: z.union([z.url('Enter a valid URL').max(2000), z.null()]).optional(),
  /** "MM-DD": the day children move up a grade. */
  schoolYearStarts: z
    .string()
    .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/, 'A day of the year, like 08-01')
    .optional(),
})
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>

/** Suggests a slug from a church name, e.g. "Grace Community" → `grace-community`. */
export function slugify(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '')
}

export const campusSchema = z.object({
  name: z.string().trim().min(1, 'Enter the campus name').max(200),
  address: z.string().trim().max(500).optional(),
  timezone: timezoneSchema.optional(),
})
export type CampusInput = z.infer<typeof campusSchema>

export const inviteMemberSchema = z.object({
  email: z.email('Enter a valid email address').max(320),
  role: z.string().min(1, 'Pick a role'),
})
export type InviteMemberInput = z.infer<typeof inviteMemberSchema>
